import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import uniq from 'lodash/uniq';

import { splitMedications } from 'src/app/components/Pages/Checkout/utils';
import type { MedicalHistoryFormValues, MedicalHistoryRequestValues } from 'src/app/types/medicalHistoryForm';
import type { ProviderDetails } from 'src/app/types/providers';
import { isNoneString } from 'src/app/utils/stringUtils';

import { pushNextCheckoutState } from '../actions/checkout';
import { getCurrentAccountId, getPotentialTransferItems, getPrimaryPatient, getPrimaryPatientId } from '../selectors';
import type { Dispatch, RootState } from '../store';

/**
 * Reducers
 */
type InitialState = {
  isSubmitting: boolean;
  isFetching: boolean;
  error: string | null;
  providers: ProviderDetails[];
};
export const initialState: InitialState = {
  isSubmitting: false,
  isFetching: false,
  error: null,
  providers: [],
};

const medicalHistoryFormSlice = createSlice({
  name: 'medicalHistoryForm',
  initialState,
  reducers: {
    fetchAccountProvidersRequest(state) {
      state.isFetching = true;
    },
    fetchAccountProvidersSuccess(state, action) {
      state.isFetching = false;
      state.providers = action.payload;
    },
    fetchAccountProvidersFailure(state, action) {
      state.isFetching = false;
      state.error = action.payload;
    },
    submitMedicalHistoryFormRequest(state) {
      state.isSubmitting = true;
    },
    submitMedicalHistoryFormSuccess(state) {
      state.isSubmitting = false;
    },
    submitMedicalHistoryFormFailure(state, action: PayloadAction<string>) {
      state.isSubmitting = false;
      state.error = action.payload;
    },
  },
});

/**
 * Thunks
 */

export const submitPatientMedicalHistory = (values: MedicalHistoryRequestValues) => {
  const { submitMedicalHistoryFormRequest, submitMedicalHistoryFormSuccess, submitMedicalHistoryFormFailure } =
    medicalHistoryFormSlice.actions;
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    const accountId = getCurrentAccountId(state);
    const patientId = getPrimaryPatientId(state);

    dispatch(submitMedicalHistoryFormRequest());
    try {
      await client.patient.updatePatient(accountId, patientId, values);
      dispatch(submitMedicalHistoryFormSuccess());
    } catch (error: any) {
      dispatch(submitMedicalHistoryFormFailure(error?.message));
      Sentry.captureException(error);
    } finally {
      dispatch(pushNextCheckoutState());
    }
  };
};

export const fetchAccountProviders = () => {
  const { fetchAccountProvidersRequest, fetchAccountProvidersSuccess, fetchAccountProvidersFailure } =
    medicalHistoryFormSlice.actions;
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    const accountId = getCurrentAccountId(state);
    dispatch(fetchAccountProvidersRequest());
    try {
      const response = await client.fetchAccountProvidersByPatient(accountId);
      let providers: ProviderDetails[] = [];
      for (const patientId in response.data) {
        if (response.data[patientId]) {
          providers = providers.concat(response.data[patientId]);
        }
      }
      dispatch(fetchAccountProvidersSuccess(providers));
    } catch (error: any) {
      dispatch(fetchAccountProvidersFailure(error?.message));
      Sentry.captureException(error);
    }
  };
};
/*
  Selectors
*/
const medicalHistoryForm = (state: RootState) => state?.medicalHistoryForm;
const initialValues: MedicalHistoryFormValues = {
  allergiesToggle: null,
  allergies: '',
  conditionsToggle: null,
  conditions: '',
  noMedicationsCheckbox: false,
  medications: [''],
  doctors: [],
};

export const getMedicalHistoryFormInitialValues = createSelector(getPrimaryPatient, primaryPatient => {
  if (!primaryPatient) {
    return initialValues;
  }
  const {
    allergies,
    medicalConditions,
    otherMedications,
  }: { allergies?: string; medicalConditions?: string; otherMedications?: string } = primaryPatient;
  return {
    allergies: allergies && !isNoneString(allergies) ? allergies : '',
    conditions: medicalConditions && !isNoneString(medicalConditions) ? medicalConditions : '',
    medications: otherMedications ? splitMedications(otherMedications) : [''],
    allergiesToggle: allergies ? !isNoneString(allergies) : null,
    conditionsToggle: medicalConditions ? !isNoneString(medicalConditions) : null,
    noMedicationsCheckbox: !!otherMedications && isNoneString(otherMedications),
  };
});

export const medicalHistoryFormIsSubmitting = createSelector(medicalHistoryForm, state => state.isSubmitting);

const getMedicalHistoryFormProviders = createSelector(
  medicalHistoryForm,
  ({ providers }) =>
    providers?.map(({ firstName, middleName, lastName, suffix }) => {
      let name = '';
      if (firstName) name += `${firstName} `;
      if (middleName) name += `${middleName} `;
      if (lastName) name += `${lastName}`;
      if (suffix) name += ` ${suffix}`;
      return name;
    }) ?? []
);

export const getProviderNames = createSelector(
  [getMedicalHistoryFormProviders, getPotentialTransferItems],
  (formProviders, potentialTransferItems) => {
    const allPotentialTransferItemProviders = potentialTransferItems.map(item => item.doctor.name);

    return uniq([...allPotentialTransferItemProviders, ...formProviders]);
  }
);

export default medicalHistoryFormSlice.reducer;
